import { useState } from 'react';
import { Loader2 } from 'lucide-react';

const USE_CASES = [
  {
    icon: '🏥',
    title: 'Medication Administration Order',
    domain: 'Healthcare',
    text: 'Administer medication to patient as needed based on symptoms. Ensure timely follow-up and monitor vitals regularly. Use clinical judgment for dosage adjustments. Notify physician if condition worsens.',
  },
  {
    icon: '🏛️',
    title: 'Departmental Compliance Directive',
    domain: 'Government Circular',
    text: 'All departments must comply with the new guidelines effective immediately. Ensure adequate measures are in place. Non-compliance will be dealt with strictly. Forward this to all relevant stakeholders and maintain proper documentation.',
  },
  {
    icon: '💻',
    title: 'Checkout Flow Requirements',
    domain: 'Product PRD',
    text: 'Checkout should be fast and secure. Notify user quickly on failure. Must work for all users across devices. Payment integration should support major providers. Add appropriate analytics tracking for business metrics.',
  },
  {
    icon: '📋',
    title: 'Employee Onboarding Process',
    domain: 'Standard Operating Procedure',
    text: 'Employee onboarding must be completed promptly. All documents should be verified before system access is granted. Assign a buddy for guidance during initial period. IT setup should be ready before joining date. Ensure compliance training is scheduled.',
  },
  {
    icon: '📄',
    title: 'Vendor Service Level Agreement',
    domain: 'Contract Clause',
    text: 'The vendor shall deliver services in a timely manner and ensure quality standards are met as per industry norms. Penalties may apply for non-adherence to agreed timelines. Support shall be provided during standard business hours with reasonable response times.',
  },
];

export default function UseCasesTab({ onTryUseCase }) {
  const [loadingIdx, setLoadingIdx] = useState(null);

  const handleTry = async (uc, i) => {
    setLoadingIdx(i);
    try {
      await onTryUseCase(uc.title, uc.text);
    } finally {
      setLoadingIdx(null);
    }
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '32px 24px' }} className="scrollbar-hide">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ color: '#f4f4f5', fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>
            One-Click Demo
          </h2>
          <p style={{ color: '#52525b', fontSize: 14, marginTop: 8 }}>
            Click "Try This" on any example to instantly analyze it through the 3-agent pipeline — switch to App tab automatically.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18 }}>
          {USE_CASES.map((uc, i) => (
            <div
              key={i}
              data-testid={`use-case-card-${i}`}
              style={{
                background: '#141419', border: '1px solid #1f1f2e',
                borderRadius: 12, padding: '22px 20px',
                display: 'flex', flexDirection: 'column', gap: 12,
                transition: 'border-color 0.2s, transform 0.2s',
              }}
              onMouseOver={e => { e.currentTarget.style.borderColor = '#2f2f45'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = '#1f1f2e'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ fontSize: 36, lineHeight: 1 }}>{uc.icon}</div>
              <div>
                <div style={{ color: '#52525b', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                  {uc.domain}
                </div>
                <h3 style={{ color: '#f4f4f5', fontSize: 15, fontWeight: 700, margin: 0, lineHeight: 1.3 }}>
                  {uc.title}
                </h3>
              </div>
              <p style={{
                color: '#52525b', fontSize: 12, lineHeight: 1.6, margin: 0,
                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {uc.text}
              </p>
              <button
                data-testid={`try-this-btn-${i}`}
                onClick={() => handleTry(uc, i)}
                disabled={loadingIdx !== null}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)',
                  borderRadius: 8, padding: '8px 16px', color: '#818cf8',
                  fontSize: 13, fontWeight: 600, cursor: loadingIdx !== null ? 'wait' : 'pointer',
                  fontFamily: 'inherit', marginTop: 'auto', transition: 'background 0.2s',
                  opacity: loadingIdx !== null && loadingIdx !== i ? 0.5 : 1,
                }}
                onMouseOver={e => { if (loadingIdx === null) e.currentTarget.style.background = 'rgba(99,102,241,0.2)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.12)'; }}
              >
                {loadingIdx === i
                  ? <><Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} /> Launching...</>
                  : <>Try This </>
                }
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 40, textAlign: 'center', padding: '24px', background: '#0d0d12', borderRadius: 12, border: '1px solid #1f1f2e' }}>
          <div style={{ color: '#52525b', fontSize: 13, lineHeight: 1.6 }}>
            Each demo runs the full 3-agent pipeline: Detection → Assessment → Rewrite. Results auto-route to the correct Kanban column.
          </div>
        </div>
      </div>
    </div>
  );
}
